import { useId, type ReactNode } from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils/cn';

export interface FormFieldControlProps {
  id: string;
  'aria-invalid': boolean | undefined;
  'aria-describedby': string | undefined;
  'aria-required': boolean | undefined;
}

interface FormFieldProps {
  label: string;
  /** Validation message, typically `formState.errors.<field>?.message`. */
  error?: string | undefined;
  description?: ReactNode;
  required?: boolean;
  className?: string;
  /** Receives the accessibility props that must be spread on the control. */
  children: (control: FormFieldControlProps) => ReactNode;
}

/**
 * Accessible label + control + help/error wiring for React Hook Form fields:
 *
 *   <FormField label="RUC" error={errors.ruc?.message} required>
 *     {(control) => <Input {...control} {...register('ruc')} />}
 *   </FormField>
 */
export function FormField({
  label,
  error,
  description,
  required = false,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {description && !error && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
